export type VippsLanguage = "dk" | "en" | "no" | "sv";

// Vipps' button ships translation bundles for { en, dk, fi, sv } plus its "no"
// source locale, keyed by its own codes - Danish is "dk", not the ISO "da" used
// everywhere else here. A code it does not recognise is discarded and the
// button silently renders Norwegian, so de/fr/nl are sent as English instead:
// a better default than Norwegian for anyone outside the Nordics. Finnish is
// omitted because the component rejects fi when brand="vipps".
const VIPPS_LANGUAGE_BY_LOCALE: Record<string, VippsLanguage> = {
  da: "dk",
  en: "en",
  no: "no",
  sv: "sv",
};

// Map Auth0 ISO language codes to supported locale codes
const localeCodeMap: Record<string, string> = {
  no: "no", // Norwegian (generic) → Norway
  nb: "no", // Norwegian Bokmål → Norway
  nn: "no", // Norwegian Nynorsk → Norway
};

// thonid.consent's LangISO639 is { No, Sv, Da, En, Fr, De, Nl } — no separate Bokmål/Nynorsk variant,
// so both collapse to "no". Every other Auth0 code already matches a variant as-is.
const languageVariantMap: Record<string, string> = {
  nb: "no",
  nn: "no",
};

export const getLanguageCode = (locale: string | undefined, fallback = "en"): string => {
  const baseLocale = locale?.toLowerCase().split(/[-_]/)[0];
  if (!baseLocale) {
    return fallback;
  }
  return languageVariantMap[baseLocale] || baseLocale;
};

export const getSupportedLocaleCode = <T extends string>(
  locale: string | undefined,
  supportedLocales: readonly T[],
  fallback: T,
): T => {
  const baseLocale = locale?.toLowerCase().split(/[-_]/)[0];

  if (!baseLocale) {
    return fallback;
  }

  const mappedLocale = localeCodeMap[baseLocale] || baseLocale;

  return supportedLocales.includes(mappedLocale as T) ? (mappedLocale as T) : fallback;
};

export const getVippsLanguage = (localeCode: string): VippsLanguage =>
  VIPPS_LANGUAGE_BY_LOCALE[localeCode] ?? "en";
